import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Form, message } from "antd";
import { SaveOutlined } from "@ant-design/icons";
import { selectLogin } from "../Login/selector";
import { getAuthUser } from "../../../utils/auth";
import { useSidebarCollapse } from "../../../utils/useSidebarCollapse";
import { API_ENDPOINT } from "../../../utils/constants";
import request from "../../../utils/request";
import PageHeader from "../../components/PageHeader";
import Sidebar from "../../components/Sidebar";
import UserDetailInjectors from "./injectors";
import PersonalDetailsCard from "./components/PersonalDetailsCard";
import UpdatePasswordCard from "./components/UpdatePasswordCard";
import VerifyEmailModal from "./components/VerifyEmailModal";
import {
  selectChangePassword,
  selectEditProfile,
  selectProfile,
  selectProfilePic,
} from "./selector";
import { userProfileUpdated } from "../Login/slice";
import {
  changePasswordReset,
  loadProfile,
  loadProfilePicture,
  profilePicUpdated,
  savePassword,
  saveProfile,
} from "./slice";
import type { UserDetailFormValues } from "./types";
import {
  CancelButton,
  FooterRow,
  InnerLayout,
  PageContent,
  PageLayout,
  SaveButton,
  SettingsForm,
  WelcomeBlock,
  WelcomeSubtitle,
  WelcomeTitle,
} from "./style";

const MAX_AVATAR_BYTES = 4 * 1024 * 1024;

export default function UserDetailPage() {
  const dispatch = useDispatch();
  const { data: loginData } = useSelector(selectLogin);
  const { data: profile } = useSelector(selectProfile);
  const { saving: pwSaving, success: pwSuccess } =
    useSelector(selectChangePassword);
  const { saving: profileSaving } = useSelector(selectEditProfile);
  const { data: profilePicUrl } = useSelector(selectProfilePic);
  const [form] = Form.useForm();

  const fallbackUser = loginData ?? getAuthUser();
  const fullName = profile?.full_name ?? fallbackUser?.full_name ?? "Guest";
  const firstName = fullName.split(" ")[0] ?? "there";
  const email = profile?.email ?? fallbackUser?.email ?? "";
  const userId = profile?.id ?? fallbackUser?.id ?? null;

  const watchedName = Form.useWatch("fullName", form);
  const watchedCurrentPw = Form.useWatch("currentPassword", form);
  const watchedNewPw = Form.useWatch("newPassword", form);
  const watchedConfirmPw = Form.useWatch("confirmPassword", form);

  const baselineName = profile?.full_name ?? fullName;
  const nameChanged = (watchedName ?? "") !== baselineName;
  const anyPasswordEntered = !!(
    watchedCurrentPw ||
    watchedNewPw ||
    watchedConfirmPw
  );
  const hasChanges = nameChanged || anyPasswordEntered;

  const [collapsed, toggleCollapsed] = useSidebarCollapse();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [avatarRemoving, setAvatarRemoving] = useState(false);
  const [localAvatarSrc, setLocalAvatarSrc] = useState<string | null>(null);

  useEffect(() => {
    dispatch(loadProfile());
    dispatch(loadProfilePicture());
  }, [dispatch]);

  useEffect(() => {
    if (profile) {
      form.setFieldsValue({ fullName: profile.full_name });
    }
  }, [profile, form]);

  useEffect(() => {
    return () => {
      if (localAvatarSrc) URL.revokeObjectURL(localAvatarSrc);
    };
  }, [localAvatarSrc]);


  useEffect(() => {
    if (pwSuccess) {
      form.resetFields(["currentPassword", "newPassword", "confirmPassword"]);
      dispatch(changePasswordReset());
    }
  }, [pwSuccess, form, dispatch]);

  async function handleAvatarSelect(file: File) {
    if (file.size > MAX_AVATAR_BYTES) {
      message.error("Image must be 4MB or smaller");
      return;
    }
    const preview = URL.createObjectURL(file);
    if (localAvatarSrc) URL.revokeObjectURL(localAvatarSrc);
    setLocalAvatarSrc(preview);
    setAvatarUploading(true);
    try {
      const formData = new FormData();
      formData.append("picture", file);
      const result = await request<{ id: number; profile_image: string }>(
        `${API_ENDPOINT}profile/picture`,
        { method: "POST", body: formData },
      );
      dispatch(profilePicUpdated(result.profile_image));
      dispatch(userProfileUpdated({ profile_pic_url: result.profile_image }));
      URL.revokeObjectURL(preview);
      setLocalAvatarSrc(null);
      message.success("Profile picture updated.");
    } catch {
      message.error("Failed to upload profile picture.");
      URL.revokeObjectURL(preview);
      setLocalAvatarSrc(null);
    } finally {
      setAvatarUploading(false);
    }
  }

  async function handleAvatarRemove() {
    setAvatarRemoving(true);
    try {
      await request(`${API_ENDPOINT}profile/picture`, { method: "DELETE" });
      if (localAvatarSrc) URL.revokeObjectURL(localAvatarSrc);
      setLocalAvatarSrc(null);
      dispatch(profilePicUpdated(null));
      dispatch(userProfileUpdated({ profile_pic_url: null }));
      message.success("Profile picture removed.");
    } catch {
      message.error("Failed to remove profile picture.");
    } finally {
      setAvatarRemoving(false);
    }
  }

  function handleCancel() {
    form.setFieldsValue({ fullName: profile?.full_name ?? fullName });
    form.resetFields(["currentPassword", "newPassword", "confirmPassword"]);
  }

  function handleSave(values: UserDetailFormValues) {
    const nameChanged = values.fullName !== (profile?.full_name ?? fullName);

    if (nameChanged) {
      dispatch(saveProfile({ full_name: values.fullName }));
    }

    const anyPassword =
      values.currentPassword || values.newPassword || values.confirmPassword;
    if (anyPassword) {
      if (!userId) {
        message.error(
          "Unable to determine your account. Please sign in again.",
        );
        return;
      }
      dispatch(
        savePassword({
          user_id: userId,
          old_password: values.currentPassword ?? "",
          new_password: values.newPassword ?? "",
          confirm_password: values.confirmPassword ?? "",
        }),
      );
    }

    if (!nameChanged && !anyPassword) {
      message.info("No changes to save.");
    }
  }

  const isSaving = pwSaving || profileSaving || avatarUploading;
  const displayAvatarSrc = localAvatarSrc ?? profilePicUrl ?? null;
  const hasAvatar = !!displayAvatarSrc;

  return (
    <PageLayout hasSider>
      <UserDetailInjectors />
      <VerifyEmailModal />
      <Sidebar
        collapsed={collapsed}
        onToggleCollapse={toggleCollapsed}
        mobileOpen={mobileNavOpen}
        onMobileClose={() => setMobileNavOpen(false)}
      />
      <InnerLayout>
        <PageHeader
          routeName="User Detail"
          breadcrumbLabel="User Detail"
          avatarUrl={displayAvatarSrc ?? undefined}
          onMenuClick={() => setMobileNavOpen(true)}
        />
        <PageContent>
          <WelcomeBlock vertical gap={4}>
            <WelcomeTitle level={2}>Welcome back, {firstName}!</WelcomeTitle>
            <WelcomeSubtitle>
              Manage personal details and update password or email
            </WelcomeSubtitle>
          </WelcomeBlock>

          <SettingsForm
            form={form}
            layout="vertical"
            requiredMark={false}
            onFinish={(values) => handleSave(values as UserDetailFormValues)}
          >
            <PersonalDetailsCard
              displayName={fullName}
              displayEmail={email}
              avatarSrc={displayAvatarSrc}
              avatarUploading={avatarUploading}
              avatarRemoving={avatarRemoving}
              hasAvatar={hasAvatar}
              onAvatarSelect={handleAvatarSelect}
              onAvatarRemove={handleAvatarRemove}
            />
            <UpdatePasswordCard isGoogleUser={!!profile?.is_google} />

            <FooterRow justify="flex-end" align="center" gap={12}>
              <CancelButton onClick={handleCancel} disabled={isSaving}>
                Cancel
              </CancelButton>
              <SaveButton
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={isSaving}
                disabled={!hasChanges || avatarUploading}
              >
                Save Changes
              </SaveButton>
            </FooterRow>
          </SettingsForm>
        </PageContent>
      </InnerLayout>
    </PageLayout>
  );
}
